import { createStore, get, set, del, keys, clear, type UseStore } from "idb-keyval";

const DB_NAME = "vroom-worker";
const STORE = "drafts";
const TTL_MS = 7 * 24 * 3600 * 1000;

let store: UseStore | null = null;

function s(): UseStore {
  if (!store) store = createStore(DB_NAME, STORE);
  return store;
}

export interface DraftRecord<T = unknown> {
  data: T;
  updatedAt: number;
  expiresAt: number;
}

export function draftKey(opts: {
  userId: string;
  formType: "work" | "no-work" | "travel" | "lodging";
  dateISO?: string;
}): string {
  return [opts.userId, opts.formType, opts.dateISO ?? "-"].join(":");
}

export async function writeDraft<T>(key: string, data: T): Promise<void> {
  const now = Date.now();
  const record: DraftRecord<T> = {
    data,
    updatedAt: now,
    expiresAt: now + TTL_MS,
  };
  await set(key, record, s());
}

export async function readDraft<T>(key: string): Promise<DraftRecord<T> | undefined> {
  return get<DraftRecord<T>>(key, s());
}

export async function deleteDraft(key: string): Promise<void> {
  await del(key, s());
}

export async function sweepDrafts(opts?: { now?: number }): Promise<number> {
  const now = opts?.now ?? Date.now();
  const allKeys = await keys<string>(s());
  let removed = 0;
  for (const key of allKeys) {
    const record = await get<DraftRecord>(key, s());
    if (record && record.expiresAt <= now) {
      await del(key, s());
      removed++;
    }
  }
  return removed;
}

export async function clearDraftsForUser(userId: string): Promise<void> {
  const allKeys = await keys<string>(s());
  for (const key of allKeys) {
    if (key.startsWith(`${userId}:`)) {
      await del(key, s());
    }
  }
}

/** Removes every draft. Intended for sign-out flows and tests. */
export async function clearAllDrafts(): Promise<void> {
  await clear(s());
}
